/* nt-attribution.js — captura y persistencia del origen de campaña (NomadTech)
 *
 * Por qué existe: el formulario de lead (nt-lead-form) enviaba `platform=web` fijo, así que
 * un lead de Google Ads era indistinguible de uno orgánico. Este módulo lee los parámetros
 * de campaña de la URL de aterrizaje, los guarda 90 días y los expone para que el formulario
 * los mande a n8n → Zapier → Holded (campos personalizados machine/ad/adset/campaign/platform).
 *
 * Modelo de atribución: **last non-direct touch**. Cada visita CON datos de campaña
 * sobrescribe la anterior; una visita directa/orgánica no borra la campaña previa.
 * Ventana: 90 días (el ciclo de venta de una CNC es largo; el clic y el formulario rara vez
 * caen el mismo día).
 *
 * Se carga desde layout/theme.liquid (todas las páginas), no solo donde está el formulario:
 * si el anuncio aterriza en la home y el usuario navega a la ficha, la query se perdería.
 *
 * API pública: window.ntAttribution.get() -> objeto plano con los campos de atribución.
 */
(function () {
  'use strict';

  var KEY = 'nt_attr';
  var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;   // 90 días
  var MAX_LEN = 200;                            // recorte defensivo por campo

  // Identificadores de clic por plataforma (los pone la propia plataforma con auto-tagging)
  var CLICK_IDS = [
    ['gclid', 'google'], ['gbraid', 'google'], ['wbraid', 'google'],
    ['msclkid', 'bing'], ['fbclid', 'meta'], ['ttclid', 'tiktok'], ['li_fat_id', 'linkedin']
  ];

  function clean(v) {
    if (v === null || v === undefined) return '';
    return String(v).trim().slice(0, MAX_LEN);
  }

  function store() {
    // localStorage puede estar bloqueado (modo privado, bloqueadores). No es motivo para fallar.
    try {
      var t = '__nt' + Date.now();
      window.localStorage.setItem(t, '1');
      window.localStorage.removeItem(t);
      return window.localStorage;
    } catch (e) {
      try { return window.sessionStorage; } catch (e2) { return null; }
    }
  }

  function params() {
    try { return new URLSearchParams(window.location.search); } catch (e) { return null; }
  }

  /** Normaliza utm_source + click id a un código de plataforma estable para Holded. */
  function platformOf(a) {
    var s = (a.utm_source || '').toLowerCase();
    var m = (a.utm_medium || '').toLowerCase();

    if (a.click_id_type === 'google') return 'google';
    if (a.click_id_type === 'bing') return 'bing';
    if (a.click_id_type === 'tiktok') return 'tiktok';
    if (a.click_id_type === 'linkedin') return 'linkedin';
    if (a.click_id_type === 'meta') {
      // fbclid lo ponen tanto Facebook como Instagram; utm_source desempata cuando existe.
      return (s.indexOf('insta') > -1 || s === 'ig') ? 'ig' : 'fb';
    }

    if (s.indexOf('google') > -1) return 'google';
    if (s.indexOf('bing') > -1 || s.indexOf('microsoft') > -1) return 'bing';
    if (s.indexOf('insta') > -1 || s === 'ig') return 'ig';
    if (s.indexOf('facebook') > -1 || s === 'fb' || s.indexOf('meta') > -1) return 'fb';
    if (s.indexOf('youtube') > -1 || s === 'yt') return 'yt';
    if (s.indexOf('linkedin') > -1) return 'linkedin';
    if (s.indexOf('tiktok') > -1) return 'tiktok';
    if (m.indexOf('email') > -1 || s.indexOf('mailjet') > -1 || s.indexOf('mailchimp') > -1) return 'email';
    if (s) return s.replace(/[^a-z0-9_-]/g, '').slice(0, 30);   // fuente nueva: se respeta tal cual
    return '';
  }

  /** Clasifica una visita sin parámetros de campaña a partir del referrer. */
  function fromReferrer() {
    var ref = '';
    try { ref = document.referrer || ''; } catch (e) { return { platform: 'web' }; }
    if (!ref) return { platform: 'web' };                       // tráfico directo

    var host = '';
    try { host = new URL(ref).hostname.toLowerCase(); } catch (e) { return { platform: 'web' }; }
    if (!host || host.indexOf(window.location.hostname) > -1) return { platform: 'web' };  // interno

    var buscadores = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'yandex.', 'qwant.'];
    for (var i = 0; i < buscadores.length; i++) {
      if (host.indexOf(buscadores[i]) === 0 || host.indexOf('.' + buscadores[i]) > -1) {
        return { platform: 'organico', referrer: host };
      }
    }
    return { platform: 'referral', referrer: host };
  }

  /** Lee la URL actual. Devuelve null si esta visita no trae ningún dato de campaña. */
  function capture() {
    var p = params();
    if (!p) return null;

    var a = { ts: Date.now() };
    var i;

    for (i = 0; i < CLICK_IDS.length; i++) {
      var v = p.get(CLICK_IDS[i][0]);
      if (v) { a.click_id = clean(v); a.click_id_type = CLICK_IDS[i][1]; break; }
    }

    var utms = ['source', 'medium', 'campaign', 'content', 'term', 'id'];
    for (i = 0; i < utms.length; i++) {
      var u = p.get('utm_' + utms[i]);
      if (u) a['utm_' + utms[i]] = clean(u);
    }

    if (!a.click_id && !a.utm_source && !a.utm_campaign) return null;   // visita sin campaña

    a.platform = platformOf(a);
    a.landing = clean(window.location.pathname);
    try { a.referrer = clean(new URL(document.referrer).hostname); } catch (e) { a.referrer = ''; }
    return a;
  }

  function load(s) {
    if (!s) return null;
    try {
      var raw = s.getItem(KEY);
      if (!raw) return null;
      var a = JSON.parse(raw);
      if (!a || !a.ts || (Date.now() - a.ts) > MAX_AGE_MS) { s.removeItem(KEY); return null; }
      return a;
    } catch (e) { return null; }
  }

  var s = store();
  var fresco = capture();
  if (fresco && s) { try { s.setItem(KEY, JSON.stringify(fresco)); } catch (e) {} }

  var attr = fresco || load(s);

  // Sin campaña conocida: se clasifica la visita actual (directo / orgánico / referral).
  if (!attr) {
    var r = fromReferrer();
    attr = { platform: r.platform, referrer: r.referrer || '', landing: clean(window.location.pathname), ts: Date.now() };
  }

  window.ntAttribution = {
    /** Campos planos listos para mandar en el formulario. */
    get: function () {
      return {
        attr_platform: attr.platform || 'web',
        attr_campaign: attr.utm_campaign || '',
        attr_adset: attr.utm_content || '',
        attr_ad: attr.utm_term || attr.utm_id || '',
        attr_source: attr.utm_source || '',
        attr_medium: attr.utm_medium || '',
        attr_click_id: attr.click_id || '',
        attr_click_id_type: attr.click_id_type || '',
        attr_referrer: attr.referrer || '',
        attr_landing: attr.landing || '',
        attr_age_days: attr.ts ? String(Math.round((Date.now() - attr.ts) / 86400000)) : ''
      };
    }
  };
})();
